/**
 * @file consent-texts-form.ts
 * @description Чистые правила формы текстов согласия (phase19.16, Р3г;
 * uiux-r3g-consent-texts §3, §5): доступ к полю по пути, нормализация перед
 * проверкой и отправкой, ошибки по полям, отличия от основы.
 *
 * Сами правила (вставки, пределы, разметка, точка в конце) — shared
 * `validateConsentTexts` и реестр `CONSENT_TEXT_FIELDS`: одна проверка на
 * кабинет и API (db-design-r3g-texts §3). Здесь только обвязка формы.
 */

import {
  consentTextFields,
  diffConsentTexts,
  validateConsentTexts,
  type ConsentTextIssue,
  type ConsentTextKind,
  type ConsentTextsByKind,
} from "@chaiberi/shared";

/** Пути полей вида в порядке реестра shared. */
export function consentTextPaths(kind: ConsentTextKind): string[] {
  return consentTextFields(kind).map((f) => f.path);
}

/** Предел длины поля из реестра shared (0 — поля нет). */
export function consentTextMax(kind: ConsentTextKind, path: string): number {
  return consentTextFields(kind).find((f) => f.path === path)?.max ?? 0;
}

/** Можно ли вставлять `{categories}`/`{services}` в поле. */
export function consentFieldTakesPlaceholders(kind: ConsentTextKind, path: string): boolean {
  const info = consentTextFields(kind).find((f) => f.path === path);
  return !!info && Object.keys(info.placeholders).length > 0;
}

// ── Доступ по пути ──

/** Дерево строк — так читаем тексты по пути, не зная их тип. */
interface TextTree {
  readonly [key: string]: string | TextTree | undefined;
}

const asTree = (value: object): TextTree => value as unknown as TextTree;

/** Значение по пути «buttons.ok»; нет поля — пустая строка. */
export function getTextAt(texts: object, path: string): string {
  let node: string | TextTree | undefined = asTree(texts);
  for (const key of path.split(".")) {
    if (typeof node !== "object" || node === null) return "";
    node = node[key];
  }
  return typeof node === "string" ? node : "";
}

function setIn(node: TextTree, keys: readonly string[], value: string): TextTree {
  const [head, ...rest] = keys;
  if (head === undefined) return node;
  const child = node[head];
  const next = rest.length === 0 ? value : setIn(typeof child === "object" && child !== null ? child : {}, rest, value);
  return { ...node, [head]: next };
}

/** Копия текстов с новым значением по пути (исходник не меняется). */
export function setTextAt<T extends object>(texts: T, path: string, value: string): T {
  return setIn(asTree(texts), path.split("."), value) as unknown as T;
}

/**
 * Перевод строки — в пробел, края — без пробелов. Сервер получает то, что
 * проверено, а человек не видит ошибку «пробел в конце», пока печатает.
 */
export function normalizeConsentTexts<K extends ConsentTextKind>(kind: K, texts: ConsentTextsByKind[K]): ConsentTextsByKind[K] {
  return consentTextPaths(kind).reduce(
    (acc, path) => setTextAt(acc, path, getTextAt(acc, path).replace(/[\r\n]+/gu, " ").trim()),
    texts,
  );
}

// ── Ошибки и отличия ──

/** Ошибки по полям: путь → первое сообщение (shared, человеческим языком). */
export type ConsentFieldErrors = ReadonlyMap<string, string>;

/** Список ошибок → карта «поле → текст» (первая ошибка поля). */
export function issuesToFieldErrors(issues: readonly ConsentTextIssue[]): ConsentFieldErrors {
  const map = new Map<string, string>();
  for (const issue of issues) if (!map.has(issue.field)) map.set(issue.field, issue.message);
  return map;
}

/** Ошибки формы по правилам shared — на нормализованных текстах. */
export function consentFieldErrors<K extends ConsentTextKind>(kind: K, texts: ConsentTextsByKind[K]): ConsentFieldErrors {
  const result = validateConsentTexts(kind, normalizeConsentTexts(kind, texts));
  return result.ok ? new Map() : issuesToFieldErrors(result.errors);
}

/** Изменённые поля относительно основы (после нормализации), в порядке формы. */
export function changedConsentFields<K extends ConsentTextKind>(
  kind: K,
  before: ConsentTextsByKind[K] | null,
  after: ConsentTextsByKind[K],
): { field: string; label: string }[] {
  const b = before === null ? null : normalizeConsentTexts(kind, before);
  return diffConsentTexts(b, normalizeConsentTexts(kind, after)).map(({ field, label }) => ({ field, label }));
}
